import { Modal, Button, Spinner, Form, Dropdown } from 'react-bootstrap';
import { useAppSelector } from '../../store/store';
import {
    getTemplateCustomTags,
    getTemplateDefaultTags,
} from '../../store/selector/template.selector';
import { useEffect, useState } from 'react';
import {
    AssetType,
    PLATFORM_ARRAY,
    Platform,
    getAssetTypeForPlatform,
} from '../../constants/campaign';
import { displayErrorToast, displaySuccessToast } from '../../utils/ToastUtils';
import { getOrganizationDetails } from '../../store/selector/organization.selector';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { IMarkProjectAsTemplateRequestpayload } from '../../pages/Apprentice/apprentice.interface';
import { useMarkProjectAsTemplateMutation } from '../../store/api/template.api';

interface IProps {
    show: boolean;
    onHide: () => void;
    projectId: number;
}

const MarkAsTemplateModal = ({ show, onHide, projectId }: IProps) => {
    const templateDefaultTags = useAppSelector(getTemplateDefaultTags);
    const templateCustomTags = useAppSelector(getTemplateCustomTags);
    const organizationDetails = useAppSelector(getOrganizationDetails);
    const activeBrand = useAppSelector(getActiveBrand);
    const [markProjectAsTemplate] = useMarkProjectAsTemplateMutation();

    const [selectedPlatform, setSelectedPlatform] = useState<Platform>(Platform.INSTAGRAM);
    const [selectedAssetType, setSelectedAssetType] = useState<AssetType>(
        getAssetTypeForPlatform(Platform.INSTAGRAM)[0],
    );
    const [inputTags, setInputTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState<string>('');
    const [tagsToShow, setTagsToShow] = useState<string[]>([]);

    const handleTagInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTagInput(e.target.value);
    };

    const addTag = (tag: string) => {
        if (tag && !inputTags.includes(tag)) {
            setInputTags([...inputTags, tag]);
        }
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        setInputTags(inputTags.filter((t) => t !== tag));
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTag(tagInput.trim());
        }
    };

    const handleSelectTag = (tag: string) => {
        addTag(tag);
    };

    const handleSubmit = () => {
        if (!activeBrand || !organizationDetails.id) return;
        if (inputTags.length === 0) {
            displayErrorToast('Please add at least one tag');
        }

        const payload: IMarkProjectAsTemplateRequestpayload = {
            projectId,
            organizationId: organizationDetails.id,
            brandId: activeBrand.id,
            platform: selectedPlatform,
            shape: selectedAssetType,
            tags: inputTags,
        };
        displaySuccessToast('Project added to the queue');
        markProjectAsTemplate(payload).then(() => {
            displaySuccessToast('Project successfully marked as template');
        });
        onHide();
    };

    useEffect(() => {
        setSelectedAssetType(getAssetTypeForPlatform(selectedPlatform)[0]);
    }, [selectedPlatform]);

    useEffect(() => {
        setTagsToShow(() => {
            const uniqueTags = Array.from(new Set([...templateCustomTags, ...templateDefaultTags]));
            return uniqueTags;
        });
    }, [templateCustomTags, templateDefaultTags]);

    return (
        <Modal centered show={show} onHide={onHide} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Add Template Details</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="alert alert-light mb-3">
                    <strong>Heads up!</strong> The details you provide below will be used to select
                    template during ads generation
                </div>

                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Platform</Form.Label>
                        <Form.Select
                            value={selectedPlatform}
                            onChange={(e) => setSelectedPlatform(e.target.value as Platform)}
                        >
                            <option value="">Select Platform</option>
                            {PLATFORM_ARRAY.map((platform) => (
                                <option key={platform} value={platform}>
                                    {platform}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Asset Type</Form.Label>
                        <Form.Select
                            value={selectedAssetType}
                            onChange={(e) => setSelectedAssetType(e.target.value as AssetType)}
                        >
                            <option value="">Select Asset Type</option>
                            {getAssetTypeForPlatform(selectedPlatform).map((assetType) => (
                                <option key={assetType} value={assetType}>
                                    {assetType}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tags</Form.Label>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '5px',
                                border: '1px solid #ccc',
                                padding: '5px',
                                borderRadius: '4px',
                            }}
                        >
                            {inputTags.map((tag) => (
                                <span
                                    key={tag}
                                    style={{
                                        backgroundColor: '#e0e0e0',
                                        color: '#333',
                                        padding: '3px 8px',
                                        borderRadius: '15px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                    }}
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        style={{
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            color: '#777',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                        }}
                                        onClick={() => removeTag(tag)}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                            <input
                                type="text"
                                value={tagInput}
                                onChange={handleTagInput}
                                onKeyDown={handleTagInputKeyDown}
                                placeholder="Enter tags or select from suggestions"
                                style={{
                                    border: 'none',
                                    outline: 'none',
                                    flex: '1',
                                    minWidth: '100px',
                                }}
                            />
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle variant="secondary" id="dropdown-basic" size="sm">
                                Suggested Tags
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {tagsToShow.map((tag) => (
                                    <Dropdown.Item key={tag} onClick={() => handleSelectTag(tag)}>
                                        {tag}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MarkAsTemplateModal;
